import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VAT_MULT = 1.21;

/**
 * Standalone Pricing page — full price grid for every space, with
 * subscription tier columns (None / Solo / Pro). Not linked from the
 * top navbar (intentionally) — only reached via the landing CTA so the
 * user never gets lost. Big back arrow at the top.
 */
export default function PricingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [spaces, setSpaces] = useState([]);
  const [plans, setPlans] = useState([]);
  const [showTVAC, setShowTVAC] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/spaces`).then((r) => r.data || []),
      axios.get(`${API}/subscriptions/plans`).then((r) => r.data || []),
    ])
      .then(([s, p]) => { setSpaces(s); setPlans(p); })
      .catch(() => {});
  }, []);

  const solo = plans.find((p) => p.plan_id === 'solo');
  const pro = plans.find((p) => p.plan_id === 'pro');
  const soloPct = Number(solo?.discount_percent || 15);
  const proPct = Number(pro?.discount_percent || 30);

  const adjust = (price) => {
    const ht = Number(price || 0);
    return showTVAC ? Math.round(ht * VAT_MULT * 100) / 100 : Math.round(ht * 100) / 100;
  };
  const discounted = (price, pct) => {
    const after = Number(price || 0) * (1 - pct / 100);
    return showTVAC ? Math.round(after * VAT_MULT * 100) / 100 : Math.round(after * 100) / 100;
  };

  const rows = (space) => [
    { key: 'hourly', label: t('pricing.hourly', { defaultValue: 'Heure' }), value: space.price_hourly },
    { key: 'half_day', label: t('booking.halfDay', { defaultValue: 'Demi-journée (4h)' }), value: space.price_half_day },
    { key: 'daily', label: t('pricing.daily', { defaultValue: 'Jour (06h–22h)' }), value: space.price_daily },
    { key: 'weekly', label: t('pricing.weekly', { defaultValue: 'Semaine' }), value: space.price_weekly },
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="pricing-page">
      {/* Sticky big back arrow */}
      <div className="sticky top-0 z-40 bg-[#F9F9F7]/80 backdrop-blur-md border-b border-[#E2E2DF]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            data-testid="pricing-back-btn"
            className="group flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-[#1A3C34] rounded-full hover:bg-[#1A3C34] hover:text-white transition-all text-[#1A3C34] font-semibold text-sm shadow-sm"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span>{t('common.back', { defaultValue: 'Retour' })}</span>
          </button>
          <div className="hidden sm:flex items-center gap-2 text-xs">
            <span className="text-[#737373]">{t('pricing.viewMode', { defaultValue: 'Affichage' })} :</span>
            <button
              type="button"
              onClick={() => setShowTVAC(false)}
              className={`px-3 py-1.5 rounded-full font-semibold transition-colors ${!showTVAC ? 'bg-[#1A3C34] text-white' : 'bg-white text-[#737373] hover:bg-[#E2E2DF]'}`}
              data-testid="pricing-toggle-ht"
            >HT</button>
            <button
              type="button"
              onClick={() => setShowTVAC(true)}
              className={`px-3 py-1.5 rounded-full font-semibold transition-colors ${showTVAC ? 'bg-[#1A3C34] text-white' : 'bg-white text-[#737373] hover:bg-[#E2E2DF]'}`}
              data-testid="pricing-toggle-tvac"
            >TVAC</button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-heading text-4xl sm:text-5xl text-[#1A3C34] tracking-tight mb-3">
          {t('pricing.pageTitle', { defaultValue: 'Nos tarifs détaillés' })}
        </h1>
        <p className="text-[#737373] max-w-2xl mb-3">
          {t('pricing.pageSubtitle', { defaultValue: 'Tous les tarifs pour chaque espace, à l\'heure, à la demi-journée, à la journée et à la semaine. Avec et sans abonnement.' })}
        </p>
        <div className="bg-[#C77D63]/10 border border-[#C77D63]/30 rounded-md px-3 py-2 inline-flex items-center gap-2 mb-10" data-testid="pricing-htva-notice">
          <span className="text-[#C77D63]">★</span>
          <span className="text-xs font-semibold text-[#C77D63]">
            {showTVAC
              ? t('pricing.noticeTvac', { defaultValue: 'Affichage TVAC (TVA 21% incluse). Une journée = 06h–22h.' })
              : t('pricing.noticeHt', { defaultValue: 'Tous les prix sont HTVA · TVA 21% en sus. Une journée = 06h–22h.' })}
          </span>
        </div>

        {/* One block per space */}
        <div className="space-y-6">
          {spaces.map((space) => (
            <div
              key={space.space_id}
              data-testid={`pricing-block-${space.space_id}`}
              className="bg-white border-2 border-[#1A3C34] rounded-2xl p-6 sm:p-8 shadow-sm"
            >
              <div className="flex items-baseline justify-between flex-wrap gap-2 mb-5">
                <h2 className="font-heading text-2xl text-[#1A3C34]">
                  {space.name || space.space_id}
                </h2>
                <span className="text-xs text-[#737373]">
                  {space.capacity} {space.capacity > 1 ? t('spaces.personsMax', { defaultValue: 'pers. max' }) : t('spaces.personMax', { defaultValue: 'pers. max' })}
                </span>
              </div>

              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-[#E2E2DF] text-[#737373]">
                      <th className="text-left py-2 px-2 font-medium">{t('pricing.duration', { defaultValue: 'Durée' })}</th>
                      <th className="text-right py-2 px-2 font-medium">{t('pricing.noSubscription', { defaultValue: 'Sans abonnement' })}</th>
                      <th className="text-right py-2 px-2 font-medium">
                        Pass Solo <span className="text-[#C77D63]">(-{soloPct}%)</span>
                      </th>
                      <th className="text-right py-2 px-2 font-medium">
                        Pass Pro <span className="text-[#C77D63]">(-{proPct}%)</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows(space).filter((r) => Number(r.value) > 0).map((r) => (
                      <tr key={r.key} className="border-b border-[#F0F0EE] hover:bg-[#FAFAF8] transition-colors">
                        <td className="py-3 px-2 text-[#1A3C34] font-medium">{r.label}</td>
                        <td className="py-3 px-2 text-right font-mono text-[#1A3C34]">
                          {adjust(r.value).toFixed(2)} EUR
                        </td>
                        <td className="py-3 px-2 text-right font-mono text-[#C77D63] font-semibold">
                          {discounted(r.value, soloPct).toFixed(2)} EUR
                        </td>
                        <td className="py-3 px-2 text-right font-mono text-[#C77D63] font-semibold">
                          {discounted(r.value, proPct).toFixed(2)} EUR
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>

        {/* CTA strip */}
        <div className="mt-12 text-center">
          <button
            type="button"
            onClick={() => navigate('/abonnements')}
            data-testid="pricing-cta-subscriptions"
            className="inline-flex items-center gap-2 bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white font-bold px-8 py-3.5 rounded-md transition-colors text-sm"
          >
            {t('pricing.subscribeCta', { defaultValue: 'Découvrir les abonnements Solo & Pro' })}
          </button>
          <p className="text-xs text-[#737373] mt-3">
            {t('pricing.subscribeHint', { defaultValue: 'Les abonnements sont mensuels, sans engagement, résiliables à tout moment.' })}
          </p>
        </div>
      </div>
    </div>
  );
}
